// A saved template's page (template-page spec §3): the section-page shell
// with the preview kept over the nav, one form for the whole look, and the
// page save bar, then the surveys a save reaches, the Danger zone and the
// template's History. Duplicate sits in the header.

import { Badge, Button, SectionNav, toast } from "@pej/kore/components";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useRef } from "react";
import { usePatchTemplate } from "@/api/templates";
import type { Template } from "@/api/types";
import { PageSaveBar } from "@/components/section-page/page-save-bar";
import { SectionPageShell } from "@/components/section-page/section-page-shell";
import { useUnsavedChangesGuard } from "@/components/section-page/use-unsaved-changes-guard";
import { HistorySection, useHistoryReveal } from "@/features/audit/entity-history";
import { countOf } from "@/lib/plural";
import { useDuplicateTemplate } from "./duplicate";
import { TemplateDangerZone } from "./template-danger-zone";
import { TemplateFormCards } from "./template-form-cards";
import { toDefaults } from "./template-schema";
import { TEMPLATE_SECTION, TemplatePreview, templateNavItems } from "./template-sections";
import { TemplateUsedBy } from "./template-used-by";
import { useTemplateForm } from "./use-template-form";

/** The header badge: "Used by 3 surveys", or "Not used". */
export function usedByLabel(count: number): string {
    return count > 0 ? `Used by ${countOf(count, "survey")}` : "Not used";
}

export function TemplatePage({ template }: { template: Template }) {
    const workspaceId = template.workspaceId;
    const navigate = useNavigate();
    const duplicating = useDuplicateTemplate(workspaceId);
    const patchTemplate = usePatchTemplate(workspaceId, template.id);
    const editor = useTemplateForm({
        defaultValues: toDefaults(template),
        persist: (body) => patchTemplate.mutateAsync(body),
        onSaved: (updated, form) => {
            // The stored template becomes the form's defaults: Discard returns
            // here, not to the `template` prop, which lags until the list
            // refetches.
            form.reset(toDefaults(updated));
            toast.success("Template saved");
        },
        failureTitle: "Save failed",
    });
    // The guard reads refs so a page action can lift it just before its own
    // navigation (delete), with no render in between.
    const dirtyRef = useRef(false);
    dirtyRef.current = editor.isDirty;
    const leavingRef = useRef(false);
    const { dialog } = useUnsavedChangesGuard(() => !leavingRef.current && dirtyRef.current);
    const used = template.surveyCount;
    const history = useHistoryReveal(TEMPLATE_SECTION.history);
    const historyScope = useMemo(
        () => ({ targetTypes: ["DesignTemplate" as const], targetIds: [template.id] }),
        [template.id],
    );

    return (
        <SectionPageShell
            backLabel="Templates"
            backLink={<Link to="/w/$workspaceId/templates" params={{ workspaceId }} />}
            title={template.name}
            info={{ id: template.id, createdAt: template.createdAt }}
            badges={<Badge tone={used > 0 ? "accent" : "neutral"}>{usedByLabel(used)}</Badge>}
            actions={
                // Copies the saved template, so not while edits are unsaved:
                // the copy would silently miss them.
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={editor.isDirty || duplicating.isPending}
                    title={editor.isDirty ? "Save or discard your changes first" : undefined}
                    onClick={() => void duplicating.duplicate(template)}
                >
                    Duplicate
                </Button>
            }
            nav={<SectionNav items={templateNavItems(true)} sticky={false} />}
            preview={<TemplatePreview values={editor.values} />}
            previewFirst
            onNavClick={history.onNavClick}
        >
            <TemplateFormCards editor={editor} />
            <TemplateUsedBy template={template} />
            <TemplateDangerZone
                template={template}
                onDeleted={() => {
                    // Deleting ends the page: unsaved edits have nothing left to belong to.
                    leavingRef.current = true;
                    navigate({ to: "/w/$workspaceId/templates", params: { workspaceId } });
                }}
            />
            <HistorySection
                workspaceId={workspaceId}
                sectionId={TEMPLATE_SECTION.history}
                scope={historyScope}
                revealed={history.revealed}
                onReveal={history.reveal}
            />
            <PageSaveBar
                dirty={editor.isDirty}
                saving={patchTemplate.isPending}
                saveLabel="Save"
                cancelLabel="Discard"
                cancelDisabled={!editor.isDirty}
                status={used > 0 ? `Saving changes every survey using this template (${used}).` : undefined}
                error={editor.error}
                onSave={editor.save}
                onCancel={editor.discard}
            />
            {dialog}
        </SectionPageShell>
    );
}
